import html2canvas from 'html2canvas'

export interface ExportTitleConfig {
    title?: string
    subtitle?: string
    fontFamily?: string
    titleFontSize?: number
    subtitleFontSize?: number
    titleColor?: string
    subtitleColor?: string
}

export interface CaptureOptions {
    fullWidth?: boolean
    scrollContainerSelector?: string
    fullWidthElementSelector?: string
    cssContextClass?: string
    hideSelectors?: string[]
    scale?: number
    backgroundColor?: string
    inPlace?: boolean
}

export interface CSVExportConfig {
    headers: string[]
    rows: (string | number | null | undefined)[][]
    filename: string
}

const DEFAULT_FONT_FAMILY = 'Poppins, Segoe UI, sans-serif'
const DEFAULT_TITLE_COLOR = '#000000'
const DEFAULT_SUBTITLE_COLOR = '#333333'
const DEFAULT_BG_COLOR = '#ffffff'
const DEFAULT_SCALE = 2

const DEFAULT_HIDE_SELECTORS = [
    '.export-menu',
    '.export-dropdown',
    '.hamburger',
    '.hamburger-btn',
    '.export-dropdown-menu',
    '.custom-download-menu'
]

export const captureElementDirect = async (
    element: HTMLElement,
    options: CaptureOptions = {}
): Promise<HTMLCanvasElement> => {
    const {
        scale = DEFAULT_SCALE,
        backgroundColor = DEFAULT_BG_COLOR,
        hideSelectors = [],
        cssContextClass,
        inPlace = false
    } = options

    if (inPlace) {
        const allHideSelectors = [...DEFAULT_HIDE_SELECTORS, ...hideSelectors]

        return await html2canvas(element, {
            scale,
            useCORS: true,
            backgroundColor,
            logging: false,
            allowTaint: true,
            ignoreElements: (el: Element) => {
                return allHideSelectors.some(sel => el.matches?.(sel))
            }
        } as any)
    }

    const rect = element.getBoundingClientRect()

    const container = document.createElement('div')
    container.style.position = 'absolute'
    container.style.left = '-9999px'
    container.style.top = '0'
    container.style.width = `${Math.ceil(rect.width)}px`
    container.style.background = backgroundColor
    container.style.fontFamily = DEFAULT_FONT_FAMILY
    container.style.pointerEvents = 'none'
    container.style.overflow = 'visible'

    let mountTarget: HTMLElement = container
    if (cssContextClass) {
        const contextWrapper = document.createElement('div')
        contextWrapper.className = cssContextClass
        contextWrapper.style.width = '100%'
        contextWrapper.style.margin = '0'
        contextWrapper.style.padding = '0'
        contextWrapper.style.background = backgroundColor
        container.appendChild(contextWrapper)
        mountTarget = contextWrapper
    }

    const clone = element.cloneNode(true) as HTMLElement
    clone.style.width = '100%'
    clone.style.maxWidth = '100%'
    clone.style.margin = '0'

    const allHideSelectors = [...DEFAULT_HIDE_SELECTORS, ...hideSelectors]
    allHideSelectors.forEach(selector => {
        clone.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = 'none'
        })
    })

    mountTarget.appendChild(clone)
    document.body.appendChild(container)

    await new Promise(resolve => setTimeout(resolve, 80))

    try {
        const canvas = await html2canvas(clone, {
            scale,
            useCORS: true,
            backgroundColor,
            logging: false,
            allowTaint: true
        } as any)
        return canvas
    } finally {
        document.body.removeChild(container)
    }
}

export const captureElementOffscreen = async (
    element: HTMLElement,
    options: CaptureOptions = {}
): Promise<HTMLCanvasElement> => {
    const rect = element.getBoundingClientRect()
    const {
        fullWidth = false,
        scrollContainerSelector,
        fullWidthElementSelector,
        cssContextClass,
        hideSelectors = [],
        scale,
        backgroundColor = DEFAULT_BG_COLOR
    } = options

    let fullScrollableWidth: number | null = null
    if (fullWidth && fullWidthElementSelector) {
        const scrollEl = element.querySelector(fullWidthElementSelector) as HTMLElement | null
        if (scrollEl) {
            fullScrollableWidth = Math.ceil(scrollEl.scrollWidth)
        }
    }

    const wrapper = document.createElement('div')
    wrapper.style.position = 'absolute'
    wrapper.style.left = '-9999px'
    wrapper.style.top = '0'
    wrapper.style.width = `${fullScrollableWidth ?? Math.ceil(rect.width)}px`
    wrapper.style.background = backgroundColor
    wrapper.style.fontFamily = DEFAULT_FONT_FAMILY
    wrapper.style.pointerEvents = 'none'

    let contextEl: HTMLElement = wrapper
    if (cssContextClass) {
        const context = document.createElement('div')
        context.className = cssContextClass
        context.style.width = '100%'
        context.style.margin = '0'
        context.style.padding = '0'
        context.style.background = backgroundColor
        wrapper.appendChild(context)
        contextEl = context
    }

    const clone = element.cloneNode(true) as HTMLElement
    clone.style.width = '100%'
    clone.style.maxWidth = '100%'
    clone.style.height = 'auto'
    clone.style.overflow = 'visible'

    if (fullWidth && fullScrollableWidth) {
        if (scrollContainerSelector) {
            const cloneScroll = clone.querySelector(scrollContainerSelector) as HTMLElement | null
            if (cloneScroll) {
                cloneScroll.style.overflow = 'visible'
                cloneScroll.style.maxWidth = 'none'
                cloneScroll.style.width = `${fullScrollableWidth}px`
            }
        }
        if (fullWidthElementSelector) {
            const cloneEl = clone.querySelector(fullWidthElementSelector) as HTMLElement | null
            if (cloneEl) {
                cloneEl.style.minWidth = `${fullScrollableWidth}px`
                cloneEl.style.width = `${fullScrollableWidth}px`
            }
        }
    }

    const allHideSelectors = [...DEFAULT_HIDE_SELECTORS, ...hideSelectors]
    allHideSelectors.forEach(selector => {
        clone.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = 'none'
        })
    })

    contextEl.appendChild(clone)
    document.body.appendChild(wrapper)

    await new Promise(resolve => setTimeout(resolve, 100))

    try {
        const effectiveScale = scale ??
            (fullScrollableWidth && fullScrollableWidth > 2200 ? 1.5 : DEFAULT_SCALE)

        return await html2canvas(clone, {
            scale: effectiveScale,
            useCORS: true,
            backgroundColor,
            logging: false,
            allowTaint: true
        } as any)
    } finally {
        document.body.removeChild(wrapper)
    }
}

export const exportToPNG = async (
    element: HTMLElement,
    filename: string,
    titleConfig?: ExportTitleConfig,
    captureOptions?: CaptureOptions
): Promise<void> => {
    const canvas = captureOptions?.fullWidth
        ? await captureElementOffscreen(element, captureOptions)
        : await captureElementDirect(element, captureOptions)
    const scale = captureOptions?.scale ?? DEFAULT_SCALE

    const hasTitle = Boolean(titleConfig?.title?.trim())
    const hasSubtitle = Boolean(titleConfig?.subtitle?.trim())

    if (!hasTitle && !hasSubtitle) {
        const link = document.createElement('a')
        link.download = `${filename}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
        return
    }

    const titleFontSize = (titleConfig?.titleFontSize ?? 16) * scale
    const subtitleFontSize = (titleConfig?.subtitleFontSize ?? 13) * scale
    const padding = 10 * scale
    
    let titleBlockHeight = padding
    if (hasTitle) titleBlockHeight += titleFontSize + padding
    if (hasSubtitle) titleBlockHeight += subtitleFontSize + padding / 2

    const finalCanvas = document.createElement('canvas')
    finalCanvas.width = canvas.width
    finalCanvas.height = canvas.height + titleBlockHeight
    const ctx = finalCanvas.getContext('2d')
    
    if (!ctx) {
        console.error('Failed to get canvas context')
        return
    }

    ctx.fillStyle = captureOptions?.backgroundColor ?? DEFAULT_BG_COLOR
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height)

    const fontFamily = titleConfig?.fontFamily ?? DEFAULT_FONT_FAMILY

    let currentY = padding
    if (hasTitle) {
        ctx.font = `bold ${titleFontSize}px ${fontFamily}`
        ctx.fillStyle = titleConfig?.titleColor ?? DEFAULT_TITLE_COLOR
        ctx.textAlign = 'center'
        currentY += titleFontSize
        ctx.fillText(titleConfig!.title!, finalCanvas.width / 2, currentY)
        currentY += padding
    }

    if (hasSubtitle) {
        ctx.font = `${subtitleFontSize}px ${fontFamily}`
        ctx.fillStyle = titleConfig?.subtitleColor ?? DEFAULT_SUBTITLE_COLOR
        ctx.textAlign = 'center'
        currentY += subtitleFontSize / 2
        ctx.fillText(titleConfig!.subtitle!, finalCanvas.width / 2, currentY)
    }

    ctx.drawImage(canvas, 0, titleBlockHeight)

    const link = document.createElement('a')
    link.download = `${filename}.png`
    link.href = finalCanvas.toDataURL('image/png')
    link.click()
}

export const exportToSVG = async (
    element: HTMLElement,
    filename: string,
    titleConfig?: ExportTitleConfig,
    captureOptions?: CaptureOptions
): Promise<void> => {
    const canvas = captureOptions?.fullWidth
        ? await captureElementOffscreen(element, captureOptions)
        : await captureElementDirect(element, captureOptions)
    const scale = captureOptions?.scale ?? DEFAULT_SCALE

    const width = canvas.width / scale
    const height = canvas.height / scale
    const dataURL = canvas.toDataURL('image/png')

    const hasTitle = Boolean(titleConfig?.title?.trim())
    const hasSubtitle = Boolean(titleConfig?.subtitle?.trim())

    const titleFontSize = titleConfig?.titleFontSize ?? 16
    const subtitleFontSize = titleConfig?.subtitleFontSize ?? 13
    const fontFamily = titleConfig?.fontFamily ?? DEFAULT_FONT_FAMILY
    const titleColor = titleConfig?.titleColor ?? DEFAULT_TITLE_COLOR
    const subtitleColor = titleConfig?.subtitleColor ?? DEFAULT_SUBTITLE_COLOR

    let titleBlockHeight = 0
    let titleY = 0
    let subtitleY = 0
    
    if (hasTitle || hasSubtitle) {
        titleBlockHeight = 10
        if (hasTitle) {
            titleY = titleBlockHeight + titleFontSize
            titleBlockHeight += titleFontSize + 8
        }
        if (hasSubtitle) {
            subtitleY = titleBlockHeight + subtitleFontSize - 3
            titleBlockHeight += subtitleFontSize + 8
        }
    }

    let titleBlock = ''
    if (hasTitle) {
        titleBlock += `  <text x="${width / 2}" y="${titleY}" font-family="${fontFamily}" font-size="${titleFontSize}" font-weight="600" text-anchor="middle" fill="${titleColor}">${escapeXml(titleConfig!.title!)}</text>\n`
    }
    if (hasSubtitle) {
        titleBlock += `  <text x="${width / 2}" y="${subtitleY}" font-family="${fontFamily}" font-size="${subtitleFontSize}" text-anchor="middle" fill="${subtitleColor}">${escapeXml(titleConfig!.subtitle!)}</text>\n`
    }

    const svgHeight = height + titleBlockHeight
    const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${svgHeight}">
  <rect width="100%" height="100%" fill="white"/>
${titleBlock}  <image x="0" y="${titleBlockHeight}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`

    const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.svg`
    link.click()
    URL.revokeObjectURL(url)
}

export const exportToCSV = (config: CSVExportConfig): void => {
    const { headers, rows, filename } = config
    
    const csvLines: string[] = []
    csvLines.push(headers.map(h => escapeCSVField(String(h))).join(','))
    rows.forEach(row => {
        const rowStr = row.map(cell => {
            if (cell === null || cell === undefined) return ''
            return escapeCSVField(String(cell))
        }).join(',')
        csvLines.push(rowStr)
    })

    const csvContent = csvLines.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${filename}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

export const exportToCSVSimple = (
    headers: string[],
    rows: (string | number)[][],
    filename: string
): void => {
    exportToCSV({ headers, rows, filename })
}

export interface ExportMenuState {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
}

export const createMenuToggleHandler = (state: ExportMenuState) => {
    return (e: React.MouseEvent) => {
        e.stopPropagation()
        state.setIsOpen(!state.isOpen)
    }
}

export const createClickOutsideHandler = (
    containerSelector: string,
    closeMenu: () => void
): (() => void) => {
    const handleClickOutside = (e: MouseEvent) => {
        if (!(e.target as Element).closest(containerSelector)) {
            closeMenu()
        }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
}

export interface ExportHandlersConfig {
    elementRef: React.RefObject<HTMLElement>
    filename: string
    titleConfig?: ExportTitleConfig
    captureOptions?: CaptureOptions
    csvConfig?: {
        headers: string[]
        getRows: () => (string | number | null | undefined)[][]
    }
}

export interface ExportHandlers {
    handleDownloadPNG: () => Promise<void>
    handleDownloadSVG: () => Promise<void>
    handleDownloadCSV: () => void
}

export const createExportHandlers = (config: ExportHandlersConfig): ExportHandlers => {
    const { elementRef, filename, titleConfig, captureOptions, csvConfig } = config

    const handleDownloadPNG = async () => {
        if (!elementRef.current) return
        await exportToPNG(elementRef.current, filename, titleConfig, captureOptions)
    }

    const handleDownloadSVG = async () => {
        if (!elementRef.current) return
        await exportToSVG(elementRef.current, filename, titleConfig, captureOptions)
    }

    const handleDownloadCSV = () => {
        if (!csvConfig) {
            console.warn('CSV export not configured')
            return
        }
        exportToCSV({
            headers: csvConfig.headers,
            rows: csvConfig.getRows(),
            filename
        })
    }

    return {
        handleDownloadPNG,
        handleDownloadSVG,
        handleDownloadCSV
    }
}

const escapeXml = (str: string): string => {
    return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')
}

const escapeCSVField = (field: string): string => {
    if (field.includes(',') || field.includes('"') || field.includes('\n')) {
        return `"${field.replace(/"/g, '""')}"`
    }
    return field
}

export const HamburgerIcon = (): JSX.Element => {
    const React = require('react')
    return React.createElement('svg', {
        width: '18',
        height: '18',
        viewBox: '0 0 24 24',
        fill: 'none',
        xmlns: 'http://www.w3.org/2000/svg'
    }, [
        React.createElement('circle', { key: 'c1', cx: '12', cy: '5', r: '2', fill: '#555' }),
        React.createElement('circle', { key: 'c2', cx: '12', cy: '12', r: '2', fill: '#555' }),
        React.createElement('circle', { key: 'c3', cx: '12', cy: '19', r: '2', fill: '#555' })
    ])
}

export const getFullWidthTableCaptureOptions = (cssContextClass: string): CaptureOptions => ({
    fullWidth: true,
    scrollContainerSelector: '.monthly-table-scroll',
    fullWidthElementSelector: '.monthly-table',
    cssContextClass
})

export interface InjectTitleConfig {
    title?: string
    subtitle?: string
    fontFamily?: string
    titleFontSize?: string
    subtitleFontSize?: string
    titleColor?: string
    subtitleColor?: string
    align?: 'left' | 'center' | 'right'
}

export const injectTitleIntoClone = (
    clone: HTMLElement,
    config: InjectTitleConfig
): HTMLElement | null => {
    if (!config.title && !config.subtitle) return null

    const align = config.align ?? 'left'
    const horizontalPadding = align === 'left' ? 'padding-left: 12px;' : 'padding-left: 12px; padding-right: 12px;'

    const titleEl = document.createElement('div')
    titleEl.className = 'chart-export-title'
    titleEl.style.cssText = `
        font-family: ${config.fontFamily ?? DEFAULT_FONT_FAMILY};
        margin-bottom: 16px;
        ${horizontalPadding}
        padding-top: 8px;
        background: white;
        text-align: ${align};
    `

    if (config.title) {
        const titleLine = document.createElement('div')
        titleLine.style.cssText = `
            font-size: ${config.titleFontSize ?? '18px'};
            font-weight: 700;
            color: ${config.titleColor ?? DEFAULT_TITLE_COLOR};
            margin-bottom: ${config.subtitle ? '4px' : '0'};
        `
        titleLine.textContent = config.title
        titleEl.appendChild(titleLine)
    }

    if (config.subtitle) {
        const subtitleLine = document.createElement('div')
        subtitleLine.style.cssText = `
            font-size: ${config.subtitleFontSize ?? '14px'};
            font-weight: 400;
            color: ${config.subtitleColor ?? DEFAULT_SUBTITLE_COLOR};
        `
        subtitleLine.textContent = config.subtitle
        titleEl.appendChild(subtitleLine)
    }

    clone.insertBefore(titleEl, clone.firstChild)
    return titleEl
}

const FONT_BOOST_CSS = `
    .export-font-boost .apexcharts-text,
    .export-font-boost .apexcharts-legend-text,
    .export-font-boost .apexcharts-yaxis-label,
    .export-font-boost .apexcharts-datalabel,
    .export-font-boost .apexcharts-title-text {
        font-size: 120% !important;
    }
    .export-font-boost .apexcharts-xaxis-label {
        font-size: 100% !important;
    }
    .export-font-boost .section-title,
    .export-font-boost .chart-title,
    .export-font-boost h2, .export-font-boost h3, .export-font-boost h4 {
        font-size: 120% !important;
    }
    .export-font-boost p,
    .export-font-boost span,
    .export-font-boost label,
    .export-font-boost td,
    .export-font-boost th,
    .export-font-boost .info-text,
    .export-font-boost .legend-text {
        font-size: 115% !important;
    }
    .export-font-boost .footer-note,
    .export-font-boost small {
        font-size: 110% !important;
    }
`

const CHART_EXPORT_HIDE_SELECTORS = [
    '.export-menu',
    '.export-dropdown',
    '.export-menu-container',
    '.chart-info-icon',
    '.hamburger',
    '.hamburger-btn'
]

const captureWithFontBoost = async (
    element: HTMLElement,
    scale: number,
    hideSelectors: string[],
    cssContextClass?: string,
    exportTitle?: string,
    exportSubtitle?: string,
    exportTitleAlign?: 'left' | 'center' | 'right',
    exportTitleFontSize?: string
): Promise<HTMLCanvasElement> => {
    const rect = element.getBoundingClientRect()

    const wrapper = document.createElement('div')
    wrapper.style.position = 'absolute'
    wrapper.style.left = '-9999px'
    wrapper.style.top = '0'
    wrapper.style.width = `${Math.ceil(rect.width)}px`
    wrapper.style.background = DEFAULT_BG_COLOR
    wrapper.style.fontFamily = DEFAULT_FONT_FAMILY
    wrapper.style.pointerEvents = 'none'
    wrapper.style.overflow = 'visible'

    let mountTarget: HTMLElement = wrapper
    if (cssContextClass) {
        const contextWrapper = document.createElement('div')
        contextWrapper.className = cssContextClass
        contextWrapper.style.width = '100%'
        contextWrapper.style.background = DEFAULT_BG_COLOR
        wrapper.appendChild(contextWrapper)
        mountTarget = contextWrapper
    }

    const clone = element.cloneNode(true) as HTMLElement
    clone.style.width = '100%'
    clone.style.maxWidth = '100%'
    clone.style.margin = '0'

    clone.querySelectorAll('.apexcharts-svg').forEach(svgEl => {
        const svg = svgEl as SVGSVGElement
        svg.style.overflow = 'visible'

        const innerGroup = svg.querySelector('.apexcharts-inner') as SVGGElement | null
        if (!innerGroup) return

        // Remove clip-path so annotations aren't clipped at chart edges
        innerGroup.removeAttribute('clip-path')
        svg.querySelectorAll('clipPath').forEach(cp => cp.remove())

        // Expand ONLY the annotation label background rects to fit boosted text (120%)
        innerGroup.querySelectorAll('.apexcharts-yaxis-annotations rect').forEach(rectEl => {
            const rw = parseFloat(rectEl.getAttribute('width') || '0')
            const rh = parseFloat(rectEl.getAttribute('height') || '0')
            const rx = parseFloat(rectEl.getAttribute('x') || '0')
            const ry = parseFloat(rectEl.getAttribute('y') || '0')
            if (rw > 0 && rh > 0) {
                const newWidth = rw * 1.25
                const newHeight = rh * 1.25
                rectEl.setAttribute('width', `${newWidth}`)
                rectEl.setAttribute('height', `${newHeight}`)
                rectEl.setAttribute('x', `${rx - (newWidth - rw)}`)
                rectEl.setAttribute('y', `${ry - (newHeight - rh) / 2}`)
            }
        })

        // Shift annotation text right to center it in the expanded rect
        innerGroup.querySelectorAll('.apexcharts-yaxis-annotations text').forEach(textEl => {
            const currentX = parseFloat(textEl.getAttribute('x') || '0')
            textEl.setAttribute('x', `${currentX + 3}`)
        })

        let innerTranslateX = 0
        let innerTranslateY = 0
        const innerTransform = innerGroup.getAttribute('transform')
        if (innerTransform) {
            const match = innerTransform.match(/translate\(\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/)
            if (match) {
                innerTranslateX = parseFloat(match[1])
                innerTranslateY = parseFloat(match[2])
            }
        }

        const annotationGroups = innerGroup.querySelectorAll('.apexcharts-yaxis-annotations')
        annotationGroups.forEach(annGroup => {
            const movedGroup = annGroup.cloneNode(true) as SVGGElement
            movedGroup.setAttribute('transform', `translate(${innerTranslateX}, ${innerTranslateY})`)
            svg.appendChild(movedGroup)
            annGroup.remove()
        })
    })

    const allHideSelectors = [...CHART_EXPORT_HIDE_SELECTORS, ...hideSelectors]
    allHideSelectors.forEach(selector => {
        clone.querySelectorAll(selector).forEach((el) => {
            (el as HTMLElement).style.display = 'none'
        })
    })

    clone.classList.add('export-font-boost')

    if (exportTitle || exportSubtitle) {
        injectTitleIntoClone(clone, {
            title: exportTitle,
            subtitle: exportSubtitle,
            align: exportTitleAlign,
            titleFontSize: exportTitleFontSize
        })
    }

    const style = document.createElement('style')
    style.textContent = FONT_BOOST_CSS
    wrapper.appendChild(style)
    mountTarget.appendChild(clone)
    document.body.appendChild(wrapper)

    await new Promise(resolve => setTimeout(resolve, 80))

    try {
        return await html2canvas(clone, {
            scale,
            useCORS: true,
            backgroundColor: DEFAULT_BG_COLOR,
            logging: false,
            allowTaint: true
        } as any)
    } finally {
        document.body.removeChild(wrapper)
    }
}

export interface ChartExportOptions {
    fontBoost?: boolean
    cssContextClass?: string
    exportTitle?: string
    exportSubtitle?: string
    exportTitleAlign?: 'left' | 'center' | 'right'
    exportTitleFontSize?: string
    extraHideSelectors?: string[]
}

export const createChartExportHandlers = (
    chartContainerRef: React.RefObject<HTMLDivElement>,
    chartTitle: string,
    filename: string,
    options: ChartExportOptions = {}
) => {
    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId)
        if (menu) menu.classList.remove('show')
    }

    const handleDownloadPNG = async (menuId: string) => {
        closeMenu(menuId)

        if (chartContainerRef.current) {
            try {
                let canvas: HTMLCanvasElement
                if (options.fontBoost || options.exportTitle) {
                    canvas = await captureWithFontBoost(
                        chartContainerRef.current,
                        DEFAULT_SCALE,
                        ['.section-title', ...(options.extraHideSelectors ?? [])],
                        options.cssContextClass,
                        options.exportTitle,
                        options.exportSubtitle,
                        options.exportTitleAlign,
                        options.exportTitleFontSize
                    )
                } else {
                    canvas = await html2canvas(chartContainerRef.current, {
                        scale: DEFAULT_SCALE,
                        useCORS: true,
                        backgroundColor: DEFAULT_BG_COLOR,
                        ignoreElements: (el: Element) =>
                            el.classList.contains('export-menu') ||
                            el.classList.contains('export-dropdown') ||
                            el.classList.contains('export-menu-container') ||
                            el.classList.contains('section-title') ||
                            el.classList.contains('chart-info-icon')
                    } as any)
                }

                const logicalWidth = canvas.width / DEFAULT_SCALE
                const logicalHeight = canvas.height / DEFAULT_SCALE
                const outputCanvas = document.createElement('canvas')
                outputCanvas.width = logicalWidth
                outputCanvas.height = logicalHeight
                const ctx = outputCanvas.getContext('2d')
                if (ctx) {
                    ctx.drawImage(canvas, 0, 0, logicalWidth, logicalHeight)
                }

                const link = document.createElement('a')
                link.download = `${filename}.png`
                link.href = (ctx ? outputCanvas : canvas).toDataURL('image/png')
                link.click()
            } catch (error) {
                console.error('Error generating PNG:', error)
            }
        }
    }

    const handleDownloadSVG = async (menuId: string) => {
        closeMenu(menuId)

        if (chartContainerRef.current) {
            try {
                let canvas: HTMLCanvasElement
                if (options.fontBoost || options.exportTitle) {
                    canvas = await captureWithFontBoost(
                        chartContainerRef.current,
                        DEFAULT_SCALE,
                        ['.section-title', ...(options.extraHideSelectors ?? [])],
                        options.cssContextClass,
                        options.exportTitle,
                        options.exportSubtitle,
                        options.exportTitleAlign,
                        options.exportTitleFontSize
                    )
                } else {
                    canvas = await html2canvas(chartContainerRef.current, {
                        scale: DEFAULT_SCALE,
                        useCORS: true,
                        backgroundColor: DEFAULT_BG_COLOR,
                        ignoreElements: (el: Element) =>
                            el.classList.contains('export-menu') ||
                            el.classList.contains('export-dropdown') ||
                            el.classList.contains('export-menu-container') ||
                            el.classList.contains('section-title') ||
                            el.classList.contains('chart-info-icon')
                    } as any)
                }

                const width = canvas.width / DEFAULT_SCALE
                const height = canvas.height / DEFAULT_SCALE
                const dataURL = canvas.toDataURL('image/png')

                const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="white"/>
  <image x="0" y="0" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`

                const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `${filename}.svg`
                link.click()
                URL.revokeObjectURL(url)
            } catch (error) {
                console.error('Error generating SVG:', error)
            }
        }
    }

    return { handleDownloadPNG, handleDownloadSVG, closeMenu }
}

export const createMenuToggle = (menuId: string) => {
    return (e: React.MouseEvent) => {
        e.stopPropagation()
        const menu = document.getElementById(menuId)
        if (menu) menu.classList.toggle('show')
    }
}

export const setupMenuClickOutside = (closeMenu: () => void): (() => void) => {
    const handleClickOutside = (e: MouseEvent) => {
        if (!(e.target as Element).closest('.export-menu-container')) {
            closeMenu()
        }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
}

const FULL_MONTH_NAMES: string[] = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
]

const formatMonthYear = (date: Date): string | null => {
    if (!(date instanceof Date) || isNaN(date.getTime())) return null
    return `${FULL_MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`
}

export const sortTimePeriods = (periods: string[], selectedTimePeriod: string): string[] => {
    if (selectedTimePeriod === "monthly") {
        return periods.sort((a, b) => {
            const monthToNumber: Record<string, number> = {
                'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
                'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
            }
            const aIsBaseline = /baseline/i.test(a)
            const bIsBaseline = /baseline/i.test(b)
            if (aIsBaseline && bIsBaseline) {
                const baselineA = a.replace(/baseline/i, '').trim()
                const baselineB = b.replace(/baseline/i, '').trim()
                const [monthA, yearA] = baselineA.includes('-') ? baselineA.split('-') : baselineA.split(' ')
                const [monthB, yearB] = baselineB.includes('-') ? baselineB.split('-') : baselineB.split(' ')
                if (parseInt(yearA) !== parseInt(yearB)) return parseInt(yearA) - parseInt(yearB)
                return (monthToNumber[monthA] || 0) - (monthToNumber[monthB] || 0)
            }
            if (aIsBaseline) return -1
            if (bIsBaseline) return 1
            let monthA, yearA, monthB, yearB
            if (a.includes(' ')) { [monthA, yearA] = a.split(' ') }
            else {
                const matchA = a.match(/([A-Za-z]+)(\d+)/)
                if (matchA) { monthA = matchA[1]; yearA = matchA[2] } else return a.localeCompare(b)
            }
            if (b.includes(' ')) { [monthB, yearB] = b.split(' ') }
            else {
                const matchB = b.match(/([A-Za-z]+)(\d+)/)
                if (matchB) { monthB = matchB[1]; yearB = matchB[2] } else return a.localeCompare(b)
            }
            if (parseInt(yearA) !== parseInt(yearB)) return parseInt(yearA) - parseInt(yearB)
            return (monthToNumber[monthA] || 0) - (monthToNumber[monthB] || 0)
        })
    } else if (selectedTimePeriod === "yearly") {
        return periods.sort((a, b) => {
            return parseInt(a.match(/\d{4}/)?.[0] || '0') - parseInt(b.match(/\d{4}/)?.[0] || '0')
        })
    } else if (selectedTimePeriod === "quarterly") {
        return periods.sort((a, b) => {
            const matchA = a.match(/Q([1-4])[-\s](\d{4})/)
            const matchB = b.match(/Q([1-4])[-\s](\d{4})/)
            if (!matchA || !matchB) return a.localeCompare(b)
            if (parseInt(matchA[2]) !== parseInt(matchB[2])) return parseInt(matchA[2]) - parseInt(matchB[2])
            return parseInt(matchA[1]) - parseInt(matchB[1])
        })
    } else if (selectedTimePeriod === "bi-yearly") {
        return periods.sort((a, b) => {
            const parseHalf = (label: string) => {
                let cleanLabel = label.trim()
                let isBaseline = cleanLabel.toLowerCase().startsWith("baseline")
                if (isBaseline) cleanLabel = cleanLabel.replace(/baseline\s*/i, "").trim()
                const match = cleanLabel.match(/(Jan-Jun|Jul-Dec)[\s-]*(\d{4})/i)
                if (match) return { half: match[1].toLowerCase().includes("jan") ? 1 : 2, year: parseInt(match[2]), isBaseline }
                const yearMatch = cleanLabel.match(/(\d{4})/)
                return { half: cleanLabel.toLowerCase().includes("jan") ? 1 : 2, year: yearMatch ? parseInt(yearMatch[1]) : 0, isBaseline }
            }
            const parsedA = parseHalf(a)
            const parsedB = parseHalf(b)
            if (parsedA.isBaseline && parsedB.isBaseline) {
                if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year
                return parsedA.half - parsedB.half
            }
            if (parsedA.isBaseline) return -1
            if (parsedB.isBaseline) return 1
            if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year
            return parsedA.half - parsedB.half
        })
    } else {
        return periods.sort((a, b) => a.localeCompare(b))
    }
}

export const getDataDueThroughDate = (): string | null => {
    const now = new Date()
    const day = now.getDate()
    const monthsBack = day < 15 ? 2 : 1
    const targetMonth = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1)
    const endOfMonth = new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0)
    return formatMonthYear(endOfMonth)
}

export const trimTrailingEmptyPeriods = (
    categories: string[],
    series: { name: string; data: (number | null)[] }[]
): { categories: string[]; series: { name: string; data: (number | null)[] }[] } => {
    if (categories.length === 0) return { categories, series }
    let lastDataIndex = -1
    for (let i = categories.length - 1; i >= 0; i--) {
        const hasData = series.some(s => s.data[i] !== null && s.data[i] !== undefined)
        if (hasData) { lastDataIndex = i; break }
    }
    if (lastDataIndex === -1) return { categories, series }
    const trimmed = lastDataIndex + 1
    return {
        categories: categories.slice(0, trimmed),
        series: series.map(s => ({ ...s, data: s.data.slice(0, trimmed) }))
    }
}

export const filterFutureMonths = (periods: string[]): string[] => {
    const now = new Date()
    const monthMap: Record<string, number> = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 }
    return periods.filter(p => {
        if (/baseline/i.test(p)) return true
        const parts = p.split(' ')
        if (parts.length !== 2) return true
        const m = monthMap[parts[0]]
        const y = parseInt(parts[1])
        if (m === undefined || isNaN(y)) return true
        return new Date(y, m) <= now
    })
}

export const getStratificationColors = (stratType: string): string[] => {
    const colorSchemes: Record<string, string[]> = {
        'insurance': ["#0072B2", "#D55E00"],
        'race': ["#0072B2", "#D55E00"],
        'race_all': ["#0072B2", "#D55E00", "#F0E441", "#009E73", "#cc79a7ff", "#56B4E9"],
        'language': ["#0072B2", "#D55E00"]
    }
    return colorSchemes[stratType] || ["#0072B2", "#D55E00"]
}

export const normalizeStratGroup = (stratType: string, rawGroup: any): string | null => {
    if (!rawGroup) return null
    const g = String(rawGroup).trim()
    if (stratType === 'race') {
        if (/^white$/i.test(g)) return 'White'
        if (/^bipoc$/i.test(g)) return 'BIPOC'
        return g
    }
    if (stratType === 'race_all' || stratType === 'allrace') {
        if (/black|african\s*american/i.test(g)) return 'Black/African American'
        if (/white\s*only/i.test(g)) return 'White'
        if (/^white$/i.test(g)) return 'White'
        if (/hispanic|latino|latina|latinx/i.test(g)) return 'Hispanic'
        if (/^asian$/i.test(g)) return 'Asian'
        if (/race\s*not\s*documented/i.test(g)) return 'Race Not Documented'
        if (/^other$/i.test(g)) return 'Other'
        return g
    }
    if (stratType === 'insurance') {
        if (/^private/i.test(g)) return 'Private'
        if (/^public/i.test(g)) return 'Public'
        return g
    }
    if (stratType === 'language') {
        if (/^english$/i.test(g)) return 'English'
        if (/^other$/i.test(g)) return 'Other'
        return g
    }
    return g
}
